// ユーザー棚卸し(GW・実データ版): 従業員名簿CSV(SmartHR・カオナビ)をアップロードし、
// メール突合で「名簿に無い Google Workspace アクティブユーザー = 退職者アカウント残存の疑い」を検出する。
// read-only: CSV はブラウザ内で処理しサーバへ送信しない。無効化の書き戻しは行わない。
// SF user-reconcile.ts のミラー(サンプル名簿ボタン/SF 固有列を除いた実データ版)。
import {
  reconcileUsers,
  rosterFromCsv,
  type RosterRow,
  type ReconcileUser,
  type ReconcileResult,
} from "./user-reconcile-logic";
import { createFileUpload } from "../ui/file-upload";
import { createDataTable, type Column } from "../ui/data-table";
import { createLoadingOverlay } from "../ui/loading-overlay";
import { showToast } from "../ui/toast";

// Directory の lastLoginTime（RFC3339）を日付表示にする。未ログイン（epoch 1970）は「ログインなし」。
function formatLastLogin(lastLoginTime: string | undefined): string {
  if (lastLoginTime === undefined || lastLoginTime === "") return "—";
  const t = Date.parse(lastLoginTime);
  if (Number.isNaN(t) || t <= 0) return "ログインなし";
  return new Date(t).toISOString().slice(0, 10);
}

function summaryCard(label: string, value: string, emphasize: boolean): HTMLElement {
  const card = document.createElement("article");
  card.className = "rounded-lg border border-slate-200 p-4";
  const h = document.createElement("h3");
  h.className = "text-sm font-medium text-slate-600";
  h.textContent = label;
  const v = document.createElement("p");
  v.className = "mt-1 text-2xl font-semibold " + (emphasize ? "text-red-700" : "text-slate-900");
  v.textContent = value;
  card.append(h, v);
  return card;
}

export interface UserReconcileDeps {
  // 接続済み GW から取得した実アクティブユーザー(email + active)。名簿 CSV はブラウザ内で突合する。
  readonly activeUsers: readonly ReconcileUser[];
}

export function renderGwUserReconcile(root: HTMLElement, deps: UserReconcileDeps): void {
  root.replaceChildren();

  const activeUsers = deps.activeUsers;

  const intro = document.createElement("p");
  intro.className = "text-sm text-slate-600";
  intro.textContent =
    "SmartHR・カオナビから取得した従業員一覧CSVをアップロードすると、メールアドレスの突合で退職者アカウントの残存（名簿に無い Google Workspace アクティブユーザー）を検出します。CSV はブラウザ内で処理され、サーバへは送信されません。";

  const alert = document.createElement("div");
  alert.setAttribute("role", "alert");
  alert.setAttribute("aria-live", "assertive");
  alert.className = "min-h-[1.25rem] text-sm text-red-700";

  const uploadHost = document.createElement("div");
  uploadHost.className = "mt-4 max-w-xl";
  uploadHost.append(
    createFileUpload({ accept: ".csv", label: "従業員一覧CSV（SmartHR・カオナビ）", onText: run }),
  );

  const resultHost = document.createElement("div");
  resultHost.className = "mt-6";

  const overlay = createLoadingOverlay({ message: "CSV を解析しています" });

  function run(text: string): void {
    alert.textContent = "";
    overlay.show();
    queueMicrotask(() => {
      const { rows, error } = rosterFromCsv(text);
      overlay.hide();
      if (error !== undefined) {
        alert.textContent = error;
        resultHost.replaceChildren();
        return;
      }
      const result = reconcileUsers(rows, activeUsers);
      renderResult(rows.length, result);
      showToast({
        tone: result.residual.length > 0 ? "warning" : "success",
        title: "棚卸しが完了しました",
        message: "残存疑い " + result.residual.length + " 件 / 突合 " + result.matchedCount + " 件",
      });
    });
  }

  function renderResult(rosterCount: number, result: ReconcileResult): void {
    resultHost.replaceChildren();

    const meta = document.createElement("p");
    meta.className = "text-sm text-slate-500";
    meta.textContent =
      "名簿 " + rosterCount + " 名と Google Workspace アクティブユーザー（実データ）を突合しました。";

    const grid = document.createElement("div");
    grid.className = "mt-3 grid gap-4 sm:grid-cols-3";
    grid.append(
      summaryCard("突合（名簿と一致）", result.matchedCount + " 名", false),
      summaryCard(
        "退職者アカウント残存の疑い",
        "残存疑い " + result.residual.length + " 件",
        result.residual.length > 0,
      ),
      summaryCard("名簿のみ（Directory 未作成・参考）", result.notInDirectory.length + " 名", false),
    );

    const residualSection = document.createElement("section");
    residualSection.className = "mt-6";
    const rh = document.createElement("h2");
    rh.className = "text-lg font-medium text-slate-900";
    rh.textContent = "退職者アカウント残存の疑い";
    const residualColumns: Column<ReconcileUser>[] = [
      { key: "email", header: "メールアドレス", cell: (u) => u.email },
      { key: "lastLogin", header: "最終ログイン", cell: (u) => formatLastLogin(u.lastLoginTime) },
    ];
    const note = document.createElement("p");
    note.className = "mt-2 text-sm text-slate-500";
    note.textContent =
      "アカウントの無効化は Google Workspace 管理コンソールでの対応が必要です（本画面からの書き戻しは行いません）。";
    residualSection.append(
      rh,
      createDataTable<ReconcileUser>({
        caption: "退職者アカウント残存の疑い一覧",
        columns: residualColumns,
        rows: result.residual,
        emptyMessage: "残存疑いはありません。",
        rowTestId: "residual-row",
      }),
      note,
    );

    const notInDirectorySection = document.createElement("section");
    notInDirectorySection.className = "mt-6";
    const nh = document.createElement("h2");
    nh.className = "text-lg font-medium text-slate-900";
    nh.textContent = "名簿のみ（Directory 未作成・参考）";
    const notInDirectoryColumns: Column<RosterRow>[] = [
      { key: "email", header: "メールアドレス", cell: (r) => r.email },
      { key: "name", header: "氏名", cell: (r) => r.name },
      { key: "status", header: "在籍状況", cell: (r) => r.status },
    ];
    notInDirectorySection.append(
      nh,
      createDataTable<RosterRow>({
        caption: "名簿に居るが Google Workspace に存在しないユーザー",
        columns: notInDirectoryColumns,
        rows: result.notInDirectory,
        emptyMessage: "該当はありません。",
        rowTestId: "not-in-directory-row",
      }),
    );

    resultHost.append(meta, grid, residualSection, notInDirectorySection);
  }

  root.append(intro, alert, uploadHost, resultHost, overlay.element);
}
